/**
 * Circuit service client — typed API calls for circuits, installs, and disconnects.
 */

import { apiClient } from '@/services/apiClient';
import type {
  DataCircuit,
  PaginatedResponse,
  CircuitLifecycleStatus,
} from '@/types';

// ─── Circuit Types ─────────────────────────────────────────────────────

export interface CircuitListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  siteNumber?: string;
  carrierVendor?: string;
  accessType?: string;
  circuitType?: string;
  lifecycleStatus?: string;
  slaStatus?: string;
  isPrimary?: boolean;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface CreateCircuitInput {
  siteNumber: string;
  circuitIdentifier?: string | null;
  accountNumber?: string | null;
  carrierVendor?: string | null;
  vendorId?: number | null;
  contractId?: number | null;
  accessType?: string | null;
  bandwidthMbps?: number | null;
  encapsulation?: string | null;
  wanIPAddress?: string | null;
  subnet?: string | null;
  mrc?: number | null;
  slaStatus?: string | null;
  circuitTypeClass?: string | null;
  primarySecondary?: string | null;
  wanAssignment?: string | null;
  wirelessFailover?: boolean;
  finalDesignFlag?: boolean;
  notes?: string | null;
}

export type UpdateCircuitInput = Partial<CreateCircuitInput> & { updatedAt: string };

// ─── Install Types ─────────────────────────────────────────────────────

export interface CircuitInstall {
  installId: number;
  circuitId: number;
  siteNumber: string;
  city: string | null;
  address: string | null;
  payToVendor: string | null;
  lec: string | null;
  circuitDetails: string | null;
  approvalStatus: string;
  billingStartDate: string | null;
  accountNumber: string | null;
  circuitServiceId: string | null;
  accessPort: string | null;
  ipAddress: string | null;
  ticketNumber: string | null;
  mrc: number | null;
  contractId: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface InstallListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  circuitId?: number;
  siteNumber?: string;
  status?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface CreateInstallInput {
  circuitId: number;
  siteNumber: string;
  city?: string | null;
  address?: string | null;
  payToVendor?: string | null;
  lec?: string | null;
  circuitDetails?: string | null;
  billingStartDate?: string | null;
  accountNumber?: string | null;
  circuitServiceId?: string | null;
  accessPort?: string | null;
  ipAddress?: string | null;
  ticketNumber?: string | null;
  mrc?: number | null;
  contractId?: number | null;
}

// ─── Disconnect Types ──────────────────────────────────────────────────

export interface CircuitDisconnect {
  disconnectId: number;
  circuitId: number;
  siteNumber: string;
  accountNumber: string | null;
  vendor: string | null;
  serviceAddress: string | null;
  disconnectReason: string | null;
  mrcSavings: number | null;
  eligibleDisconnectDate: string | null;
  modemReturnStatus: string | null;
  rmaNumber: string | null;
  hardwareReturnStatus: string | null;
  hardwareLocation: string | null;
  returnCarrier: string | null;
  trackingNumber: string | null;
  notes: string | null;
  isComplete: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DisconnectListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  circuitId?: number;
  siteNumber?: string;
  isComplete?: boolean;
  hardwareReturnStatus?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface CreateDisconnectInput {
  circuitId: number;
  siteNumber: string;
  accountNumber?: string | null;
  vendor?: string | null;
  serviceAddress?: string | null;
  disconnectReason?: string | null;
  eligibleDisconnectDate?: string | null;
  rmaNumber?: string | null;
  hardwareReturnStatus?: string | null;
  returnCarrier?: string | null;
  trackingNumber?: string | null;
  notes?: string | null;
}

export interface BulkImportResult {
  imported: number;
  updated: number;
  errors: Array<{ row: number; error: string }>;
}

// ─── Service ───────────────────────────────────────────────────────────

export const circuitService = {
  // Circuits
  list(params?: CircuitListParams): Promise<PaginatedResponse<DataCircuit>> {
    return apiClient.get('circuits', params as Record<string, string | number | boolean | undefined>);
  },

  getById(circuitId: number): Promise<DataCircuit> {
    return apiClient.get(`circuits/${circuitId}`);
  },

  create(input: CreateCircuitInput): Promise<DataCircuit> {
    return apiClient.post('circuits', input);
  },

  update(circuitId: number, input: UpdateCircuitInput): Promise<DataCircuit> {
    return apiClient.put(`circuits/${circuitId}`, input);
  },

  delete(circuitId: number): Promise<void> {
    return apiClient.delete(`circuits/${circuitId}`);
  },

  transitionLifecycle(circuitId: number, targetStatus: CircuitLifecycleStatus): Promise<DataCircuit> {
    return apiClient.put(`circuits/${circuitId}/lifecycle`, { targetStatus });
  },

  bulkImport(circuits: CreateCircuitInput[]): Promise<BulkImportResult> {
    return apiClient.post('circuits/import', { circuits });
  },

  // Installs
  listInstalls(params?: InstallListParams): Promise<PaginatedResponse<CircuitInstall>> {
    return apiClient.get('circuits/installs', params as Record<string, string | number | boolean | undefined>);
  },

  getInstall(installId: number): Promise<CircuitInstall> {
    return apiClient.get(`circuits/installs/${installId}`);
  },

  createInstall(input: CreateInstallInput): Promise<CircuitInstall> {
    return apiClient.post('circuits/installs', input);
  },

  updateInstall(installId: number, input: Record<string, unknown>): Promise<CircuitInstall> {
    return apiClient.put(`circuits/installs/${installId}`, input);
  },

  transitionInstallStatus(installId: number, targetStatus: string): Promise<CircuitInstall> {
    return apiClient.put(`circuits/installs/${installId}/status`, { targetStatus });
  },

  deleteInstall(installId: number): Promise<void> {
    return apiClient.delete(`circuits/installs/${installId}`);
  },

  // Disconnects
  listDisconnects(params?: DisconnectListParams): Promise<PaginatedResponse<CircuitDisconnect>> {
    return apiClient.get('circuits/disconnects', params as Record<string, string | number | boolean | undefined>);
  },

  getDisconnect(disconnectId: number): Promise<CircuitDisconnect> {
    return apiClient.get(`circuits/disconnects/${disconnectId}`);
  },

  createDisconnect(input: CreateDisconnectInput): Promise<CircuitDisconnect> {
    return apiClient.post('circuits/disconnects', input);
  },

  updateDisconnect(disconnectId: number, input: Record<string, unknown>): Promise<CircuitDisconnect> {
    return apiClient.put(`circuits/disconnects/${disconnectId}`, input);
  },

  completeDisconnect(disconnectId: number): Promise<CircuitDisconnect> {
    return apiClient.put(`circuits/disconnects/${disconnectId}/complete`);
  },

  deleteDisconnect(disconnectId: number): Promise<void> {
    return apiClient.delete(`circuits/disconnects/${disconnectId}`);
  },
};
